import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, CheckCircle, AlertCircle, RefreshCw, Settings } from 'lucide-react';
import { getJobberAuthUrl, exchangeCodeForToken } from '../services/jobberConfig';
import { JobberService } from '../services/jobberService';

const JobberIntegration = ({ onClose, currentUser }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const userId = currentUser?.uid;

  const handleOAuthCallback = useCallback(async (code, state) => {
    if (!userId) return;
    setIsLoading(true);
    setError('');

    try {
      const tokenData = await exchangeCodeForToken(code);
      await JobberService.storeTokens(tokenData, userId);
      setIsConnected(true);
      setSuccess('Successfully connected to Jobber!');
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('OAuth callback error:', error);
      setError('Failed to connect to Jobber: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setIsConnected(false);
      return;
    }
    (async () => {
      await JobberService.isAuthenticatedWithRefresh(userId);
      setIsConnected(JobberService.isAuthenticated());
    })();

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    if (code && state) {
      handleOAuthCallback(code, state);
    }
  }, [userId, handleOAuthCallback]);

  const handleConnect = () => {
    if (!userId) {
      setError('You must be signed in to connect Jobber.');
      return;
    }
    const authUrl = getJobberAuthUrl(userId);
    console.log('Generated Jobber Auth URL:', authUrl);
    console.log('Opening Jobber OAuth in popup...');
    
    // Open popup and monitor for completion
    const popup = window.open(authUrl, 'jobber-oauth', 'width=600,height=700,scrollbars=yes,resizable=yes');
    
    if (!popup) {
      console.error('❌ Failed to open popup - popup blocked?');
      setError('Popup blocked! Please allow popups for this site and try again.');
      return;
    }
    
    console.log('✅ Popup opened successfully');
    
    // Monitor popup for completion
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        console.log('🚪 Popup was closed');
        clearInterval(checkClosed);
        // Check if we have new tokens (user completed OAuth)
        if (JobberService.isAuthenticated()) {
          console.log('✅ OAuth completed successfully via popup close detection');
          setIsConnected(true);
          setSuccess('Successfully connected to Jobber!');
        } else {
          console.log('❌ OAuth not completed - popup closed without success');
        }
      }
    }, 1000);
    
    // Also listen for message from popup (if OAuth completes successfully)
    const handleMessage = (event) => {
      console.log('📨 Received message from popup:', event);
      console.log('🌐 Event origin:', event.origin);
      console.log('🌐 Window origin:', window.location.origin);
      console.log('📝 Event data:', event.data);
      
      if (event.origin !== window.location.origin) {
        console.log('❌ Origin mismatch, ignoring message');
        return;
      }
      
      if (event.data.type === 'JOBBER_OAUTH_SUCCESS') {
        console.log('✅ Received OAuth success message');
        clearInterval(checkClosed);
        popup.close();
        if (userId) JobberService.loadTokensForUser(userId).then(() => setIsConnected(true));
        else setIsConnected(true);
        setSuccess('Successfully connected to Jobber!');
        window.removeEventListener('message', handleMessage);
      } else if (event.data.type === 'JOBBER_OAUTH_ERROR') {
        console.log('❌ Received OAuth error message:', event.data.error);
        clearInterval(checkClosed);
        popup.close();
        setError('Failed to connect to Jobber: ' + event.data.error);
        window.removeEventListener('message', handleMessage);
      } else {
        console.log('❓ Unknown message type:', event.data.type);
      }
    };
    
    window.addEventListener('message', handleMessage);
  };

  const handleDisconnect = async () => {
    if (userId) await JobberService.clearTokens(userId);
    setIsConnected(false);
    setSuccess('');
    setError('');
  };

  const handleTestConnection = async () => {
    if (!userId) return;
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await JobberService.testConnection(userId);
      setSuccess(result.message);
    } catch (error) {
      setError('Connection test failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-secondary rounded-2xl p-6 shadow-lg border border-primary w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-primary">Jobber Integration</h2>
          </div>
          <button
            onClick={onClose}
            className="btn btn-secondary p-2 rounded-xl"
          >
            ×
          </button>
        </div>

        {/* Connection Status */}
        <div className={`rounded-xl p-4 mb-6 ${isConnected ? 'bg-sky-100 border border-sky-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center gap-3">
            {isConnected ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            <div>
              <h3 className={`font-semibold ${isConnected ? 'text-black' : 'text-primary'}`}>
                {isConnected ? 'Connected to Jobber' : 'Not Connected'}
              </h3>
              <p className={`text-sm ${isConnected ? 'text-black' : 'text-tertiary'}`}>
                {isConnected 
                  ? 'Your app is connected to Jobber. Quotes can be synced automatically.'
                  : 'Connect to Jobber to sync quotes and manage customers.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          </div>
        )}

        {/* Integration Features */}
        <div className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold text-primary">Integration Features</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-secondary rounded-xl p-4 border border-primary">
              <h4 className="font-medium text-primary mb-2">Customer Sync</h4>
              <p className="text-sm text-tertiary">
                Automatically sync customer information between your quote app and Jobber.
              </p>
            </div>
            
            <div className="bg-secondary rounded-xl p-4 border border-primary">
              <h4 className="font-medium text-primary mb-2">Quote Export</h4>
              <p className="text-sm text-tertiary">
                Export quotes directly to Jobber as jobs or estimates.
              </p>
            </div>
            
            <div className="bg-secondary rounded-xl p-4 border border-primary">
              <h4 className="font-medium text-primary mb-2">Real-time Updates</h4>
              <p className="text-sm text-tertiary">
                Keep your data synchronized across both platforms.
              </p>
            </div>
            
            <div className="bg-secondary rounded-xl p-4 border border-primary">
              <h4 className="font-medium text-primary mb-2">Workflow Integration</h4>
              <p className="text-sm text-tertiary">
                Seamlessly move from quotes to job scheduling and management.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {!isConnected ? (
            <button
              onClick={handleConnect}
              disabled={isLoading}
              className={`btn btn-primary flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <ExternalLink className="w-5 h-5" />
              {isLoading ? 'Connecting...' : 'Connect to Jobber'}
            </button>
          ) : (
            <>
              <button
                onClick={handleTestConnection}
                disabled={isLoading}
                className={`btn btn-secondary flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <RefreshCw className="w-5 h-5" />
                {isLoading ? 'Testing...' : 'Test Connection'}
              </button>
              
              <button
                onClick={handleDisconnect}
                className="btn btn-secondary flex-1 py-3 rounded-xl font-semibold"
              >
                Disconnect
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default JobberIntegration;
